import "./admin_tools.scss";

import { useState, type ChangeEvent, type ComponentProps } from "react";
import { FormattedMessage } from "react-intl";

import Row from "common/details/grid/row";
import Section from "common/details/grid/section";
import Link from "common/core/link";
import WorkflowModalV2 from "common/modals/workflow_modal/v2";
import Button from "common/core/button";
import { StyledTextInput } from "common/form/inputs/text";
import { useMutation } from "util/graphql";
import { usePermissions } from "common/core/current_user_role";

import ForceCompleteTransactionMutation from "./force_complete_transaction_mutation.graphql";
import RetryPostMeetingJobMutation from "./retry_post_meeting_job_mutation.graphql";

const MEETING_NOT_FOUND_MESSAGE = (
  <FormattedMessage
    id="1d7cbc1f-8ecb-4fb2-b457-46e5e2353375"
    defaultMessage="There is no completed meeting to retry, the meeting might be ongoing or was terminated by the notary."
  />
);
const UNAUTHORIZED_USER_MESSAGE = (
  <FormattedMessage
    id="8285ec19-d270-4852-8e4b-9a844794166f"
    defaultMessage="You are not authorized for administrative tool because you are not a super admin."
  />
);
const RETRY_FAILED_MESSAGE = (
  <FormattedMessage id="ccccab9b-7a03-4183-83f6-242a3dc071fb" defaultMessage="The retry failed." />
);

type Props = {
  organizationTransactionId: string;
  documentBundleId: string;
};

type RetryState =
  | null
  | { tag: "error"; message: string }
  | { tag: "success" }
  | { tag: "loading" };

function AdminTools(props: Props) {
  const [showForceCompleteModal, setForceCompleteModal] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [completionReason, setCompletionReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const forceCompleteTransaction = useMutation(ForceCompleteTransactionMutation);

  const [retry, setRetry] = useState<RetryState>(null);
  const [showRetryMeetingModal, setRetryMeetingModal] = useState(false);
  const [retryReason, setRetryReason] = useState("");

  const retryPostMeetingJob = useMutation(RetryPostMeetingJobMutation);

  const { hasPermissionFor } = usePermissions();

  const buttons: ComponentProps<typeof WorkflowModalV2>["buttons"] =
    retry?.tag === "success"
      ? [
          <Button
            key="exit"
            onClick={() => {
              setRetryMeetingModal(false);
            }}
            buttonColor="action"
            variant="primary"
          >
            <FormattedMessage id="e305177e-bbd2-42eb-8f8a-71aed834370a" defaultMessage="Exit" />
          </Button>,
        ]
      : [
          <Button
            key="cancel"
            disabled={retry?.tag === "loading"}
            onClick={() => {
              setRetryMeetingModal(false);
            }}
            buttonColor="action"
            variant="primary"
          >
            <FormattedMessage id="66335b94-f4e4-45ab-9361-abbb9fc88cb9" defaultMessage="Cancel" />
          </Button>,
          <Button
            key="confirm"
            isLoading={retry?.tag === "loading"}
            disabled={retryReason.trim() === ""}
            onClick={() => {
              setRetry({ tag: "loading" });

              retryPostMeetingJob({
                variables: {
                  input: {
                    documentBundleId: props.documentBundleId,
                    retryReason,
                  },
                },
              })
                .then(() => {
                  setRetry({ tag: "success" });
                })
                .catch((e) => {
                  const message = e.graphQLErrors?.[0]?.message;
                  const retryErrMsg =
                    message === "not_found"
                      ? MEETING_NOT_FOUND_MESSAGE
                      : message === "unauthorized"
                      ? UNAUTHORIZED_USER_MESSAGE
                      : null;
                  setRetry({
                    tag: "error",
                    message: retryErrMsg || e.message || RETRY_FAILED_MESSAGE,
                  });
                });
            }}
            buttonColor="action"
            variant="primary"
          >
            <FormattedMessage id="cd342acc-fef6-48c7-a8a4-032b70ee5703" defaultMessage="Confirm" />
          </Button>,
        ];
  return (
    <>
      <Section>
        <Row
          title={
            <FormattedMessage
              id="5b4f4b98-0f6b-47db-b483-a2fc4c5510e9"
              defaultMessage="Admin Tools"
            />
          }
        >
          <div className="NotarizationDetails--AdminTools">
            <ul className="NotarizationDetails--AdminTools--list">
              <li className="NotarizationDetails--AdminTools--list--item">
                <Link
                  automationId="retry-meeting-link"
                  onClick={() => {
                    setRetryMeetingModal(true);
                    setRetryReason("");
                    setRetry(null);
                  }}
                >
                  <FormattedMessage
                    id="5a4b82fb-817f-4ddf-b432-18174b9bacbe"
                    defaultMessage="Retry Meeting Processing"
                  />
                </Link>
                <div className="NotarizationDetails--AdminTools--list--item--secondary">
                  <FormattedMessage
                    id="c59fcd8e-0f56-405b-8432-9a65b15e095d"
                    defaultMessage="This tool retries meeting processing"
                  />
                </div>
              </li>
            </ul>
          </div>
        </Row>
        {showRetryMeetingModal && (
          <WorkflowModalV2
            title={
              <FormattedMessage
                id="2526019d-cbb4-4ef1-b9b7-1cd460e7bba2"
                defaultMessage="Confirm Retry"
              />
            }
            buttons={buttons}
            footerSeparator={false}
            large
          >
            {retry?.tag === "error" ? (
              <p className="NotarizationDetails--AdminTools--error">
                <FormattedMessage
                  id="984ad196-0b80-42f9-8dd0-5b105bdcd44e"
                  defaultMessage="Error: {errorMessage}"
                  values={{ errorMessage: retry.message }}
                />
              </p>
            ) : retry?.tag === "success" ? (
              <p>
                <FormattedMessage
                  id="71dbedf8-ad97-4d03-a988-f726e448df4c"
                  defaultMessage="You successfully scheduled retrying the meeting."
                />
              </p>
            ) : (
              <>
                <FormattedMessage
                  id="a88f6d6b-57d1-4089-b54d-14049565b88f"
                  defaultMessage="This feature should be used before the force complete option. It will re-run completing the meeting. This is the first step the on-call engineer would try in this case."
                />
                <div className="NotarizationDetails--AdminTools--input">
                  <StyledTextInput
                    placeholder={
                      <FormattedMessage
                        id="1cf2d736-6410-4d44-85e1-418516cf1eab"
                        defaultMessage="Why are you retrying this meeting's processing?"
                      />
                    }
                    displayRequiredAsterisk
                    value={retryReason}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setRetryReason(e.target.value);
                    }}
                  />
                </div>
              </>
            )}
          </WorkflowModalV2>
        )}
        <Row>
          <div className="NotarizationDetails--AdminTools">
            {hasPermissionFor("forceCompleteTransactions") && (
              <ul className="NotarizationDetails--AdminTools--list">
                <li className="NotarizationDetails--AdminTools--list--item">
                  <Link
                    automationId="force-complete-link"
                    onClick={() => {
                      setForceCompleteModal(true);
                      setCompletionReason("");
                    }}
                  >
                    <FormattedMessage
                      id="9dc4a601-ea65-4b30-a5c8-c05ef90d8c77"
                      defaultMessage="Force Complete/Finalize Transaction"
                    />
                  </Link>
                  <div className="NotarizationDetails--AdminTools--list--item--secondary">
                    <FormattedMessage
                      id="3b57e97b-7175-4949-86a7-a1a2132aa7dd"
                      defaultMessage="Rejects all non-completed documents and marks transaction as complete"
                    />
                  </div>
                </li>
              </ul>
            )}
          </div>
        </Row>
      </Section>
      {showForceCompleteModal && (
        <WorkflowModalV2
          title={
            <FormattedMessage
              id="1601ab60-bf14-45a5-8e3e-b71fc88f8b6d"
              defaultMessage="Confirm Force Complete"
            />
          }
          buttons={[
            <Button
              key="cancel"
              automationId="cancel-force-complete"
              onClick={() => {
                setLoading(false);
                setForceCompleteModal(false);
              }}
              buttonColor="dark"
              variant="tertiary"
            >
              <FormattedMessage id="ce035113-aa6a-40ca-97d0-98c2426d084c" defaultMessage="Cancel" />
            </Button>,
            <Button
              key="confirm"
              automationId="confirm-force-complete"
              onClick={() => {
                setLoading(true);
                forceCompleteTransaction({
                  variables: {
                    input: {
                      organizationTransactionId: props.organizationTransactionId,
                      completionReason,
                    },
                  },
                })
                  .then(() => {
                    setForceCompleteModal(false);
                  })
                  .catch((e) => {
                    setError(e.graphQLErrors?.[0]?.message || e.message);
                  })
                  .finally(() => {
                    setLoading(false);
                  });
              }}
              isLoading={isLoading}
              buttonColor="danger"
              variant="primary"
              disabled={completionReason.trim() === ""}
            >
              <FormattedMessage
                id="7bda6c45-7cb5-40af-b44d-f4ebe897fff6"
                defaultMessage="Confirm"
              />
            </Button>,
          ]}
          footerSeparator={false}
          large
        >
          {error && (
            <p className="NotarizationDetails--AdminTools--error">
              <FormattedMessage
                id="2fbd2c45-91e4-4097-869d-08dacf1ffbe7"
                defaultMessage="Error: {error}"
                values={{ error }}
              />
            </p>
          )}
          <FormattedMessage
            id="7c16b1c7-5209-4601-8129-3428f1ac7fd0"
            defaultMessage="This will reject all non-completed documents and prevent any further signings. This can take a few minutes, please refresh the page to see results. Please note:"
          />
          <ul className="NotarizationDetails--AdminTools--bullets">
            <li>
              <FormattedMessage
                id="261fe2be-ba21-4db4-93f7-78582dcc740c"
                defaultMessage="This feature should mainly be used on a multisigner transaction where Signer A completes their portion of the transaction, but signer B didn't complete it in time. However, the organization still wants to use Signer A's documents."
              />
            </li>
            <li>
              <FormattedMessage
                id="eb02ebf5-76d3-4b81-8b80-dd66fbd99694"
                defaultMessage="This will work for both Hybrid and Full RON transactions."
              />
            </li>
            <li>
              <FormattedMessage
                id="cfb85404-c94a-4c95-ab19-9f8dde75389f"
                defaultMessage="You cannot use this feature when a meeting is in progress for the transaction"
              />
            </li>
            <li>
              <FormattedMessage
                id="887e4cd1-e980-4a42-9606-8c2c5f2ee849"
                defaultMessage="This change will be logged for security and tracking purposes in the Activity Log"
              />
            </li>
            <li>
              <FormattedMessage
                id="3dc01bda-b94f-403e-850c-ac69b8f487a3"
                defaultMessage="This will finalize eNotes in their current state"
              />
            </li>
          </ul>
          <div className="NotarizationDetails--AdminTools--input">
            <StyledTextInput
              placeholder={
                <FormattedMessage
                  id="3c25e18f-0088-4e2c-b88c-ea426ebb9752"
                  defaultMessage="Why you are force completing this transaction"
                />
              }
              displayRequiredAsterisk
              value={completionReason}
              data-automation-id="force-complete-reason"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setCompletionReason(e.target.value);
              }}
            />
          </div>
        </WorkflowModalV2>
      )}
    </>
  );
}

export default AdminTools;
